import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { createMachine, assign, sendParent } from 'xstate';
import { TagPair } from '~app/shared/map/layer/layer-definitions/T14';
import { ValmetDnaService } from '../valmet-dna.service';
import {
  ReceivedTagMeasurementsEvent, ReceivedTagMetadataEvent, RequestedTag, TagErrorEvent, TagEvent,
  TagMachine, TagServiceContext, TagServiceEvent, TagServiceState
} from './neve-measurements-tag.interface';
import { NeveMeasurementsModule } from './neve-measurements.module';

export const getFullTag = (tagPair: TagPair) => `${tagPair[0]}:${tagPair[1]}`;

const tagMetadataError: TagErrorEvent = {
  type: 'TAG.ERROR',
  stage: 'metadata',
};

const tagMeasurementError: TagErrorEvent = {
  type: 'TAG.ERROR',
  stage: 'measurements',
};

@Injectable({
  providedIn: NeveMeasurementsModule
})
export class NeveMeasurementsTagService {
  private readonly TAG_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
  constructor(
    private readonly valmetDna: ValmetDnaService
  ) { }

  public createNewTagMachine(tagpair: TagPair): TagMachine {
    return this.createTagMachine(tagpair);
  }

  private createRequestedTag(tagPair: TagPair): RequestedTag {
    return {
      tagPair,
      mainTag: tagPair[0],
      subTag: tagPair[1],
      fullTag: getFullTag(tagPair)
    };
  }

  private createTagMachine(tagpair: TagPair): TagMachine {
    return createMachine<TagServiceContext, TagServiceEvent, TagServiceState>({
      id: `tag-${getFullTag(tagpair)}`,
      initial: 'initializing',
      context: {
        config: {
          requestedTag: this.createRequestedTag(tagpair),
          measurementRefreshIntervalMs: this.TAG_REFRESH_INTERVAL_MS
        },
        events$: new Subject<TagEvent>(),
        latestEvents: {},
        requestTimestamps: {},
      },
      states: {
        'initializing': {
          always: {
            actions: sendParent(context => ({
              type: 'TAG.INITIALIZED',
              context
            })),
            target: 'waiting-for-refresh',
          },
        },
        'waiting-for-refresh': {
          on: {
            'REFRESH': [
              { target: 'fetching-metadata', cond: (context) => !context.latestEvents.metadata },
              'fetching-measurements',
            ],
          }
        },
        'fetching-metadata': {
          entry: [
            assign({
              requestTimestamps: (context) => ({
                ...context.requestTimestamps,
                latest: new Date(),
                metadata: new Date(),
              })
            }),
            (context, event) => context.events$.next({
              type: 'REQUESTED_TAG_METADATA',
              requestedTag: context.config.requestedTag
            })
          ],
          invoke: {
            id: 'fetchMetadata',
            src: 'fetchTagMetadata',
            onError: {
              target: 'waiting-for-refresh',
              actions: [
                (context, event) => console.error(
                  `TagMachine("${context.config.requestedTag.fullTag}").fetchMetadata`,
                  event?.data?.message,
                ),
                assign({
                  latestEvents: (context, event) => ({
                    ...context.latestEvents,
                    error: tagMetadataError,
                  })
                }),
                (context, event) => context.events$.next(tagMetadataError),
                sendParent((context): TagErrorEvent => tagMetadataError),
              ]
            },
            onDone: {
              target: 'waiting-for-refresh',
              actions: [
                assign({
                  latestEvents: (context, event) => ({
                    ...context.latestEvents,
                    metadata: event.data,
                    error: undefined
                  })
                }),
                (context, event) => context.events$.next(event.data),
                sendParent((context, event) => event.data),
              ],
            }
          }
        },
        'fetching-measurements': {
          entry: [
            assign({
              requestTimestamps: (context) => ({
                ...context.requestTimestamps,
                latest: new Date(),
                measurements: new Date(),
              })
            }),
          ],
          invoke: {
            id: 'fetchMeasurements',
            src: 'fetchTagMeasurements',
            onError: {
              target: 'waiting-for-refresh',
              actions: [
                (context, event) => console.error(
                  `TagMachine("${context.config.requestedTag.fullTag}").fetchMeasurements`,
                  event?.data?.message,
                ),
                assign({
                  latestEvents: (context, event) => ({
                    ...context.latestEvents,
                    error: tagMeasurementError,
                  })
                }),
                (context, event) => context.events$.next(tagMeasurementError),
                sendParent((context): TagErrorEvent => tagMeasurementError),
              ]
            },
            onDone: {
              target: 'waiting-for-refresh',
              actions: [
                assign({
                  latestEvents: (context, event) => ({
                    ...context.latestEvents,
                    measurements: event.data,
                    error: undefined
                  })
                }),
                (context, event) => context.events$.next(event.data),
                sendParent((context, event) => event.data),
              ],
            }
          }
        },
      }
    }, {
      services: {
        fetchTagMetadata: async (context): Promise<ReceivedTagMetadataEvent> => {
          const tagsResult = await this.valmetDna.getTags(context.config.requestedTag.mainTag);
          return {
            type: 'RECEIVED_TAG_METADATA',
            metadata: {
              tagsResult,
              requestedTag: context.config.requestedTag,
              tag: tagsResult.Tag.find(t => t.TagName === context.config.requestedTag.fullTag),
            },
          };
        },
        fetchTagMeasurements: async (context): Promise<ReceivedTagMeasurementsEvent> => {
          const trendResult = await this.valmetDna.getTrend(context.config.requestedTag.mainTag, context.config.requestedTag.subTag);
          return {
            type: 'RECEIVED_TAG_MEASUREMENTS',
            measurements: trendResult
          };
        }
      }
    });
  }

}
